import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
    cloneDeep as _cloneDeep,
} from 'lodash';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';

import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';

import PolicyCard from '../../../../../components/policycard';
import SearchBar from '../../../../../components/searchbar';
import ConfirmationDialog from '../../../../../components/confirmationdlg';
import Validator, { useValidate, checkValidity } from '../../../../../components/validator';

import PolicyService from '../../../../../data/policy-service';
import Auth from '../../../../../data/auth-service';

const POLICY_TYPE = 'datanode_system_config';

const useStyles = makeStyles(theme => ({
    accordionStrip: {
        background: 'transparent',
        border: 'none',
        boxShadow: 'none',
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    highlightText: {
        color: theme.palette.secondary.main,
    },
}));

const DatanodeSNMP = () => {
    const classes = useStyles();
    const [configs, setConfigs] = useState([]);
    const [showCreate, setShowCreate] = useState(false);
    const [edit, setEdit] = useState(false);
    const [configName, setConfigName] = useState('');
    const [editData, setEditData] = useState(null);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const configForm = useValidate({});
    const snmpPerms = Auth.permissions.get('Cluster');

    const updateConfigs = useCallback((filterFn) => {
        PolicyService.getDatanodePolicies(POLICY_TYPE).then(res => {
            const list = Array.isArray(res) ? res : [];
            if (filterFn) {
                filterFn(list);
            } else {
                setConfigs(list);
            }
        }, err => {
            console.error('Unable to get configurations ::: ', err);
        });
    }, []);

    useEffect(() => {
        if (snmpPerms.read) {
            updateConfigs();
        }
    }, [snmpPerms.read, updateConfigs]);

    const toggleCreate = () => {
        setShowCreate(!showCreate);
    };

    const handleUpdateName = (e, validID, valid) => {
        if (validID) {
            configForm.set({
                ...configForm.get(),
                [validID]: valid,
            });
        }
        setConfigName(e.target.value.replace(' ', ''));
    };

    const handleEditConfig = (p) => {
        setShowCreate(true);
        setConfigName(p.name);
        setEdit(true);
        setEditData({ ...p });
    };

    const handleCancelCreate = () => {
        setShowCreate(false);
        setEdit(false);
        setConfigName('');
        setEditData(null);
    };

    const handleSubmitConfig = () => {
        if (edit) {
            PolicyService.updateDatanodePolicy(POLICY_TYPE, editData.name, {
                name: configName,
                type: POLICY_TYPE,
            }).then(() => {
                updateConfigs();
                toast.success('Configuration updated successfully');
            }, err => {
                console.error(err);
                toast.error('Failed to update configuration');
            });
        } else {
            PolicyService.createDatanodePolicy(POLICY_TYPE, configName, '').then(() => {
                updateConfigs();
                toast.success('Configuration created successfully');
            }, err => {
                console.error(err);
                toast.error(`Cannot create a configuration with the name '${configName}'`);
            });
        }
        handleCancelCreate();
    };

    const handleSearchChange = ({ search }) => {
        updateConfigs((p) => {
            p = p.filter(x => x.name.toLowerCase().match((search || '').toLowerCase()));
            setConfigs(p);
        });
    };

    const handleOpenDelete = (p) => {
        setSelectedConfig(p);
        setDeleteOpen(true);
    };

    const handleCancelDelete = () => {
        setSelectedConfig(null);
        setDeleteOpen(false);
    };

    const handleDeleteConfig = () => {
        PolicyService.deleteDatanodePolicy(POLICY_TYPE, selectedConfig.name).then(() => {
            setSelectedConfig(null);
            setDeleteOpen(false);
            updateConfigs();
            updateDnCounts();
            toast.success('Configuration deleted successfully');
        }, err => {
            console.error(err);
            toast.error('An error occurred deleting the configuration...');
            setSelectedConfig(null);
            setDeleteOpen(false);
        });
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <Box>
                    <SearchBar immediate onSearch={handleSearchChange} searchservice={{ ...PolicyService, searchPlaceholder: 'Search configs' }} />
                </Box>
                <Box display="flex">
                    {snmpPerms.write && <Box>
                        <Button aria-label="create configuration" variant="contained" color="primary" onClick={toggleCreate}>
                            Create Configuration
                        </Button>
                    </Box>}
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Accordion expanded={showCreate} display={showCreate ? 'flex' : 'none'} className={classes.accordionStrip}>
                    <AccordionSummary style={{ display: 'none' }} />
                    <AccordionDetails>
                        <Grid container spacing={2} justifyContent="center">
                            <Grid container item xs={9}>
                                <Grid item xs={12}>
                                    <Validator
                                        valueType="policyName"
                                        errorText="Configuration name invalid"
                                        fullWidth
                                        placeholder="Configuration name"
                                        onChange={handleUpdateName}
                                        value={configName}
                                    />
                                </Grid>
                                <Grid item container xs={12} justifyContent="flex-end">
                                    {snmpPerms.write && <Box mt={2} display="flex">
                                        <Box><Button
                                            variant="contained"
                                            color="secondary"
                                            aria-label="Cancel"
                                            onClick={handleCancelCreate}
                                        >Cancel</Button></Box>
                                        <Box ml={2}><Button
                                            variant="contained"
                                            color="primary"
                                            aria-label={edit ? 'Update' : 'Create'}
                                            onClick={handleSubmitConfig}
                                            disabled={!checkValidity(configForm.get())}
                                        >{edit ? 'Update' : 'Create'}</Button></Box>
                                    </Box>}
                                </Grid>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            </Grid>
            <Grid container item xs={12} spacing={2}>
                {configs.map((p, i) => (
                    <Grid item xs={4} key={'dn-config-' + i}>
                        <PolicyCard
                            title={p.name}
                            subtitle={<Box>Datanodes: {p.count || 0}</Box>}
                            route={`/system/configuration/snmp/datanode/${p.name}`}
                            action={<Box p={1} display={p.name === 'default' ? 'none' : 'block'}>
                                {snmpPerms.write && <Box>
                                    <IconButton
                                        aria-label="edit configuration"
                                        onClick={() => { handleEditConfig(p) }}
                                        size="large"><SettingsIcon /></IconButton>
                                </Box>}
                                {snmpPerms.write && <Box display={p.count ? 'none' : 'block'}>
                                    <IconButton
                                        color="secondary"
                                        aria-label="delete configuration"
                                        onClick={() => { handleOpenDelete(p) }}
                                        size="large"><DeleteIcon /></IconButton>
                                </Box>}
                            </Box>}
                            color={p.color || ''}
                            type={POLICY_TYPE}
                            source={p.source || ''}
                        />
                    </Grid>
                ))}
            </Grid>
            <ConfirmationDialog
                onClose={handleCancelDelete}
                onOK={handleDeleteConfig}
                open={deleteOpen}
                message={<Box>
                    Are you sure you want to delete the configuration <span className={classes.highlightText}>{(selectedConfig && selectedConfig.name) || ''}</span>?
                </Box>}
                title="Delete Configuration"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
        </Grid>
    );
};

export default DatanodeSNMP;
